import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";

import type { LocalDateInternal } from "../../../../../common/calendar/LocalDateInternal";
import type { LocalTimeInternal } from "../../../../../common/calendar/LocalTimeInternal";
import { parseDecimal } from "../../../../../common/UnitParser";
import { nameOf } from "../../../../../common/Utils";
import { useGetCuttingLengths } from "../../../../../service/settings/useGetCuttingLengths";
import { useGetHumidityLevels } from "../../../../../service/settings/useGetHumidityLevels";
import { useGetWoodTypes } from "../../../../../service/settings/useGetWoodTypes";
import type {
  OrderFormData,
  OrderItemsContextProps,
  OrderItemArticleData,
  OrderItemWoodData,
} from "../contexts/FormContexts";
import { CustomerFormContext, DeliveryFormContext, OrderItemType, OrderItemsContext } from "../contexts/FormContexts";
import type { OrderForm } from "../hooks/useOrderForm";

interface ProviderProps {
  children: ReactNode;
  form: OrderForm;
}

// Helpers: keep pure and outside of component for clarity
function resolveUnitPrice(price: unknown, override: unknown): number {
  const o = parseDecimal(override);
  const p = parseDecimal(price);
  return o ?? p ?? 0;
}

function sumWoodItems(items: OrderItemWoodData[]): number {
  let total = 0;
  for (const item of items) total += resolveUnitPrice(item.price, item.priceOverride);
  return total;
}

function sumArticleItems(items: OrderItemArticleData[]): number {
  let total = 0;
  for (const item of items) {
    const unit = resolveUnitPrice(item.price, item.priceOverride);
    const qty = parseDecimal(item.quantity) ?? 0;
    total += unit * qty;
  }
  return total;
}

export function CustomerFormProvider({
  children,
  form,
  showContactSelect,
}: ProviderProps & {
  showContactSelect: boolean;
}) {
  const validateAddresses = useCallback(() => {
    const hasInvoiceAddress = Boolean(form.values.invoiceAddress?.trim());
    const needsDeliveryAddress = form.values.deliveryAddressDifferent;
    const hasDeliveryAddress = Boolean(form.values.deliveryAddress?.trim());

    return hasInvoiceAddress && (!needsDeliveryAddress || hasDeliveryAddress);
  }, [form.values]);

  const state = useMemo(
    () => ({
      isAddressValid: validateAddresses(),
      hasSelectedContact: form.values.contactId !== undefined,
    }),
    [form.values.contactId, validateAddresses],
  );

  const setDeliveryAddress = useCallback(
    (address?: string) => {
      form.setFieldValue(`${nameOf<OrderFormData>().deliveryAddress}`, address);
    },
    [form],
  );

  const clearDeliveryAddress = useCallback(() => {
    form.setFieldValue(`${nameOf<OrderFormData>().deliveryAddress}`, undefined);
  }, [form]);

  const value = {
    form,
    showContactSelect,
    state,
    setDeliveryAddress,
    clearDeliveryAddress,
    validateAddresses,
  };

  return <CustomerFormContext.Provider value={value}>{children}</CustomerFormContext.Provider>;
}

export function DeliveryFormProvider({ children, form }: ProviderProps) {
  const setDateRange = useCallback(
    (start: LocalDateInternal, end?: LocalDateInternal) => {
      form.setFieldValue(`${nameOf<OrderFormData>().deliveryTargetDateStart}`, start);
      form.setFieldValue(`${nameOf<OrderFormData>().deliveryTargetDateEnd}`, end);
    },
    [form],
  );

  const setTimeRange = useCallback(
    (start: LocalTimeInternal, end?: LocalTimeInternal) => {
      form.setFieldValue(`${nameOf<OrderFormData>().deliveryTargetTimeStart}`, start);
      form.setFieldValue(`${nameOf<OrderFormData>().deliveryTargetTimeEnd}`, end);
    },
    [form],
  );

  const validateDeliveryRange = useCallback(() => {
    const hasStartDate = Boolean(form.values.deliveryTargetDateStart);
    const hasValidEndDate =
      !form.values.deliveryTargetDateEnd ||
      (form.values.deliveryTargetDateStart &&
        form.values.deliveryTargetDateEnd >= form.values.deliveryTargetDateStart) ||
      false;
    const hasValidTimeRange =
      !form.values.deliveryTargetTimeStart ||
      !form.values.deliveryTargetTimeEnd ||
      form.values.deliveryTargetTimeEnd > form.values.deliveryTargetTimeStart;

    return hasStartDate && hasValidEndDate && hasValidTimeRange;
  }, [form.values]);

  const state = useMemo(
    () => ({
      isDateValid: Boolean(form.values.deliveryTargetDateStart),
      isTimeValid: validateDeliveryRange(),
      hasValidRange: validateDeliveryRange(),
    }),
    [form.values.deliveryTargetDateStart, validateDeliveryRange],
  );

  const value = {
    form,
    state,
    setDateRange,
    setTimeRange,
    validateDeliveryRange,
  };

  return <DeliveryFormContext.Provider value={value}>{children}</DeliveryFormContext.Provider>;
}

export function OrderItemsProvider({ children, form }: ProviderProps) {
  const calculateTotalPrice = useCallback(
    () => sumWoodItems(form.values.itemsWood) + sumArticleItems(form.values.itemsArticle),
    [form.values.itemsWood, form.values.itemsArticle],
  );

  const validateItems = useCallback(() => {
    const hasValidWoodItems = form.values.itemsWood.every(
      (item) => item.woodTypeId && item.cuttingLengthId && item.humidityLevelId && item.volume && item.price,
    );
    const hasValidArticleItems = form.values.itemsArticle.every((item) => item.sku && item.quantity && item.price);
    return hasValidWoodItems && hasValidArticleItems;
  }, [form.values.itemsWood, form.values.itemsArticle]);

  const state = useMemo(
    () => ({
      totalPrice: calculateTotalPrice(),
      itemCount: form.values.itemsWood.length + form.values.itemsArticle.length,
      isValid: validateItems(),
    }),
    [form.values.itemsWood, form.values.itemsArticle, calculateTotalPrice, validateItems],
  );

  const { data: cuttingLengthsData } = useGetCuttingLengths();
  const { data: humidityLevelsData } = useGetHumidityLevels();
  const { data: woodTypesData } = useGetWoodTypes();

  const handleAddWoodItem = () => {
    form.insertListItem(`${nameOf<OrderFormData>().itemsWood}`, {
      index: form.values.itemsWood.length,
      type: OrderItemType.WOOD,
      cuttingLengthId: cuttingLengthsData?.cuttingLengths?.[0]?.cuttingLengthId,
      humidityLevelId: humidityLevelsData?.humidityLevels?.[0]?.humidityLevelId,
      woodTypeId: woodTypesData?.woodTypes?.[0]?.woodTypeId,
      unitOfMeasurement: "SOLID_CUBIC_METER",
    });
  };

  const handleAddArticleItem = () => {
    form.insertListItem(`${nameOf<OrderFormData>().itemsArticle}`, {
      index: form.values.itemsArticle.length,
      type: OrderItemType.ARTICLE,
      quantity: 1,
    });
  };

  const handleAddItem = (type: OrderItemType) => {
    if (type === OrderItemType.WOOD) {
      handleAddWoodItem();
    } else {
      handleAddArticleItem();
    }
  };

  const handleRemoveItem = (index: number, type: OrderItemType) => {
    const listName =
      type === OrderItemType.WOOD ? nameOf<OrderFormData>().itemsWood : nameOf<OrderFormData>().itemsArticle;
    form.removeListItem(listName, index);
  };

  const value = {
    form,
    state,
    onAddItem: handleAddItem,
    onRemoveItem: handleRemoveItem,
    calculateTotalPrice,
    validateItems,
    contextData: {
      wood: {
        woodTypes: woodTypesData?.woodTypes,
        cuttingLengths: cuttingLengthsData?.cuttingLengths,
        humidityLevels: humidityLevelsData?.humidityLevels,
      },
    },
  } satisfies OrderItemsContextProps;

  return <OrderItemsContext.Provider value={value}>{children}</OrderItemsContext.Provider>;
}
